import {
  TrackingElementContext,
  TrackingProduct,
  TrackingProductStreamElementContext,
  TrackingSectionContext,
} from '../../../types';
import {getCategory} from '../helpers/category';
import {
  DataLayerProductGA4,
  DetailedPageInfo,
  EventPageData,
  InitialPageInfo,
  TrackingClientStateHolder,
} from '../helpers/clientState';
import {shouldPushToDataLayerObject} from '../helpers/datalayer';
import {getStateFullDataLayerProduct} from '../helpers/product';
import {trackSortChannel} from '../helpers/sortChannel';
import {addContainerToHistory} from '../helpers/sourceHistory';

interface ProductImpressionEventGA4 extends EventPageData {
  event: 'view_item_list';
  ecommerce: {
    items: DataLayerProductGA4[];
  };
}

interface ProductClickEventGA4 extends EventPageData {
  event: 'select_item';
  ecommerce: {
    items: [DataLayerProductGA4];
  };
}

export interface ProductClickEvent {
  GA4: ProductClickEventGA4;
}

export interface ProductImpressionEvent {
  GA4: ProductImpressionEventGA4;
}

export const trackProductInteraction =
  ({
    type,
    section,
    element,
    product,
    trackPreviousPage,
    disabled,
  }: {
    type: 'impression' | 'click';
    section: TrackingSectionContext;
    element: TrackingElementContext | TrackingProductStreamElementContext;
    product: TrackingProduct;
    trackPreviousPage?: boolean;
    disabled?: boolean;
  }) =>
  (
    clientState: TrackingClientStateHolder
  ):
    | ProductClickEvent
    | ProductImpressionEvent
    | 'no event data for trackProductInteraction'
    | 'expected no event data for product impressions' => {
    let sortChannel: string | undefined;
    if (section.type === 'stream') {
      if (type === 'click') {
        trackSortChannel(clientState)({section});
      }
      sortChannel = section.sortChannel;
    }

    if (type === 'click') {
      addContainerToHistory({section, clientState});
    }

    const currentState = clientState.getCurrent();
    const category = getCategory({
      productCategories: null,
      state: currentState,
    });

    const {product: productGA} = getStateFullDataLayerProduct({
      currentState,
      element,
      section,
      product,
      variant: undefined,
      sortChannel,
      category,
      productUnitPrice: product.unitPrice,
    });

    const previousPage =
      currentState.pageHistory[currentState.pageHistory.length - 2];

    const pageToTrack = trackPreviousPage
      ? previousPage
      : currentState.currentPage;

    if (type === 'impression' && !disabled) {
      triggerProductImpression({
        clientState,
        dataLayerProduct: {
          GA4: productGA,
        },
        currentPage: pageToTrack,
      });
      return 'expected no event data for product impressions';
    } else if (type === 'click') {
      const eventGA4: ProductClickEventGA4 = {
        event: 'select_item',
        content_name: pageToTrack?.page?.path || '',
        page_type: pageToTrack?.page?.type || '',
        page_type_id:
          pageToTrack?.type === 'detailed'
            ? `${pageToTrack.page?.id || ''}`
            : '',
        ecommerce: {
          items: [productGA],
        },
      };

      return {
        GA4: eventGA4,
      };
    }

    return 'no event data for trackProductInteraction';
  };

const submitImpressionsToDataLayer = ({
  clientState,
  currentPage,
}: {
  clientState: TrackingClientStateHolder;
  currentPage?: InitialPageInfo | DetailedPageInfo;
}) => {
  let currentState = clientState.getCurrent();
  clearTimeout(currentState.productImpressionQueueTimeout);
  currentState = clientState.update({
    productImpressionQueueTimeout: undefined,
  });

  const eventGA4: ProductImpressionEventGA4 = {
    event: 'view_item_list',
    content_name: currentPage?.page?.path || '',
    page_type: currentPage?.page?.type || '',
    page_type_id:
      currentPage?.type === 'detailed' ? `${currentPage.page?.id || ''}` : '',
    ecommerce: {
      items: [...currentState.productImpressionQueue.GA4],
    },
  };

  const event = {
    GA4: eventGA4,
  };

  shouldPushToDataLayerObject(event, clientState);
};

const triggerProductImpression = ({
  clientState,
  dataLayerProduct,
  currentPage,
}: {
  clientState: TrackingClientStateHolder;
  dataLayerProduct: {
    GA4: DataLayerProductGA4;
  };
  currentPage?: InitialPageInfo | DetailedPageInfo;
}) => {
  let currentState = clientState.getCurrent();

  currentState = clientState.update({
    productImpressionQueue: {
      GA4: [...currentState.productImpressionQueue.GA4, dataLayerProduct.GA4],
    },
  });

  if (currentState.productImpressionQueue.GA4.length === 6) {
    submitImpressionsToDataLayer({
      clientState,
      currentPage,
    });
    currentState = clientState.update({
      productImpressionQueue: {
        GA4: [],
      },
    });
  } else if (currentState.productImpressionQueueTimeout === undefined) {
    currentState = clientState.update({
      productImpressionQueueTimeout: window.setTimeout(() => {
        submitImpressionsToDataLayer({
          clientState,
          currentPage,
        });
        clientState.update({
          productImpressionQueue: {
            GA4: [],
          },
        });
      }, 1000),
    });
  }
  return currentState;
};
